/**
 * OKC (eski kart seti Türk kahvesi) ayar sekmesi — saf yardımcılar (#325).
 * Sözleşme: BayinerIOT-Workspace-/docs/api-contracts/okc-settings.md
 *
 * Şema sunucudan gelir (gruplar + öğeler: tip, sınır, adım, birim, seçenek);
 * burada yalnız panel tarafı dönüşümler var.
 */

/** OKC makinesi mi — 4G kartının deviceInfo.hwVersion'ı "OKC63" gibi. */
export const isOkcDevice = (device) =>
  /^OKC\d+$/.test(String(device?.deviceInfo?.hwVersion ?? ""));

/** Bekleyen değişiklik anahtarı: makine kapsamında göz yok. */
export const changeId = (key, cup) =>
  cup === undefined || cup === null ? `${key}:-` : `${key}:${cup}`;

/** Öğenin (göz) değeri. */
export const itemValue = (item, cup) =>
  item.scope === "cup" ? (Array.isArray(item.value) ? item.value[cup] : undefined) : item.value;

/** Ekranda gösterilecek etiket — birim ekli: "Pişirme ısı (°C)". */
export const itemLabel = (item) => (item.unit ? `${item.label} (${item.unit})` : item.label);

/**
 * SettingsFieldCard'ın beklediği alan nesnesi. bool → Checkbox, enum →
 * SelectOptions (id = seçenek sırası), int → Number.
 */
export const toFieldSetting = (item, value) => {
  const base = { label: itemLabel(item), disabled: !!item.disabled };
  if (item.type === "bool") return { ...base, type: "Checkbox", value: value === 1 || value === true };
  if (item.type === "enum") {
    return {
      ...base,
      type: "SelectOptions",
      value,
      options: (item.options || []).map((label, id) => ({ id, label })),
    };
  }
  return { ...base, type: "Number", value, lowerBound: item.min, upperBound: item.max };
};

/** Karttan gelen değeri sözleşmenin tamsayısına çevirir. */
export const normalizeValue = (item, v) => {
  if (item.type === "bool") return v ? 1 : 0;
  return v;
};

/** Değer hatası (Türkçe) ya da null. */
export const validateValue = (item, v) => {
  if (item.type === "bool") return v === 0 || v === 1 ? null : "Geçersiz değer";
  if (typeof v !== "number" || !Number.isInteger(v)) return "Tam sayı girin";
  if (v < item.min) return `En az ${item.min} olmalı`;
  if (v > item.max) return `En fazla ${item.max} olmalı`;
  if (item.step > 1 && (v - item.min) % item.step !== 0) return `${item.step}'in katı olmalı`;
  return null;
};

/** Gösterim metni: seçenekli öğede seçenek adı, sayıda birimli. */
export const formatValue = (item, v) => {
  if (v === undefined || v === null || v === "") return "—";
  if (item.options) return item.options[v] ?? String(v);
  return item.unit ? `${v} ${item.unit}` : String(v);
};

const REASON_TEXT = {
  unknown_key: "Makine bu ayarı tanımıyor",
  invalid_type: "Geçersiz değer",
  out_of_range: "Aralık dışında",
  bad_step: "Adım değerine uymuyor",
  bad_cup: "Geçersiz göz",
  forbidden: "Yetkiniz yok",
  not_ready: "Makine hazır değil",
  no_display: "Ekran kartına ulaşılamadı",
  persist_failed: "Makine kaydedemedi",
  timeout: "Makine cevap vermedi",
};

export const reasonText = (reason) => REASON_TEXT[reason] || reason || "Bilinmeyen hata";

/** Sunucuya gidecek changes[] — pending: { [changeId]: { key, cup?, value } }. */
export const buildChanges = (pending) =>
  Object.values(pending).map(({ key, cup, value }) =>
    cup === undefined ? { key, value } : { key, cup, value },
  );

/** Kabul edilen değerleri okuma verisine yazar (iyimser önbellek güncellemesi). */
export const applyAccepted = (data, changes, results) => {
  if (!data || !Array.isArray(data.groups)) return data;
  const ok = new Set(
    (results || []).filter((r) => r && r.success === true).map((r) => changeId(r.key, r.cup)),
  );
  const byId = new Map(changes.map((c) => [changeId(c.key, c.cup), c]));
  return {
    ...data,
    groups: data.groups.map((g) => ({
      ...g,
      items: g.items.map((item) => {
        if (item.scope === "cup") {
          if (!Array.isArray(item.value)) return item;
          let touched = false;
          const value = item.value.map((v, cup) => {
            const id = changeId(item.key, cup);
            if (ok.has(id) && byId.has(id)) {
              touched = true;
              return byId.get(id).value;
            }
            return v;
          });
          return touched ? { ...item, value } : item;
        }
        const id = changeId(item.key);
        return ok.has(id) && byId.has(id) ? { ...item, value: byId.get(id).value } : item;
      }),
    })),
  };
};

/** Değişiklik listesinde gösterilecek ad: "Göz 2 · Pişirme ısı". */
export const changeTitle = (item, cup) =>
  cup === undefined ? item.label : `Göz ${cup + 1} · ${item.label}`;

/**
 * Sözleşme 1.2 (#364): öğeleri gelen sırayı bozmadan konu bloklarına böler.
 * Konu yoksa (1.1 sunucusu) tek başlıksız blok.
 */
export const topicBlocks = (items) => {
  const blocks = [];
  (items || []).forEach((it) => {
    const topic = it.topic || null;
    const last = blocks[blocks.length - 1];
    if (last && last.topic === topic) last.items.push(it);
    else blocks.push({ topic, items: [it] });
  });
  return blocks;
};

// Yeni makinedeki gibi (BeverageMachine cupConstants MIXER_DEPENDENT_KEYS):
// otomatik karıştırma açıkken manuel hız kullanılmaz, kapalıyken eğri.
// Ekran kartı da aynı kuralla çalışır (stm32_display brew_process.c
// mixer_cook_hw).
const MIXER_DEPENDENT_KEYS = new Set(["mixerSpeed", "halfPowMixerPct"]);
const AUTO_MIXER_CURVE_KEYS = new Set(["mixKneeTemp", "mixPlateauHw", "mixStepHw", "mixStepDown"]);

/** Bu gözde gösterilecek öğe mi — autoMixer, göz için o anki değer (0/1). */
export const isShownForAutoMixer = (item, autoMixer) => {
  if (autoMixer === undefined) return true;
  if (autoMixer === 1 && MIXER_DEPENDENT_KEYS.has(item.key)) return false;
  if (autoMixer === 0 && AUTO_MIXER_CURVE_KEYS.has(item.key)) return false;
  return true;
};

/** Arama: etiket, konu ya da bölüm adında geçiyor mu (büyük/küçük harf duyarsız). */
export const matchesSearch = (item, query, sectionLabel = "") => {
  const q = String(query || "").trim().toLocaleLowerCase("tr");
  if (!q) return true;
  return [item.label, item.topic, sectionLabel]
    .filter(Boolean)
    .some((s) => s.toLocaleLowerCase("tr").includes(q));
};
